import React, { useCallback, useRef } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import type { ADFEntity } from '@atlaskit/adf-utils/types';
import { templateGalleryMessages } from '@atlaskit/editor-common/messages';
import { logException } from '@atlaskit/editor-common/monitoring';
import type { Selection } from '@atlaskit/editor-prosemirror/state';
import { Transform } from '@atlaskit/editor-prosemirror/transform';
import { ToolbarDropdownItem } from '@atlaskit/editor-toolbar/toolbar-dropdown-item';
import { ToolbarDropdownItemSection } from '@atlaskit/editor-toolbar/toolbar-dropdown-item-section';
import { ToolbarNestedDropdownMenu } from '@atlaskit/editor-toolbar/toolbar-nested-dropdown-menu';
import SaveIcon from '@atlaskit/icon-lab/core/save';
import TemplateIcon from '@atlaskit/icon-lab/core/template';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import SearchIcon from '@atlaskit/icon/core/search';
import Lozenge from '@atlaskit/lozenge/lozenge';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { Box } from '@atlaskit/primitives/compiled';

import type { SnippetsPlugin } from '../../snippetsPluginType';
import { openTemplateGallerySafely } from '../../utils/openTemplateGallery';
import { getCreateSnippetRequest } from '../toolbar/getCreateSnippetToolbarComponents';

const messages = defineMessages({
	saveBlockTemplate: {
		id: 'editor-plugin-snippets.block-menu.TemplateGalleryBlockMenuItem.saveBlockTemplate',
		defaultMessage: 'Save block template',
		description: 'Label for saving the selected editor content as a block template',
	},
});

type Props = {
	api: Parameters<SnippetsPlugin>[0]['api'];
	onCreateSnippet?: Parameters<SnippetsPlugin>[0]['config']['onCreateSnippet'];
	onOpenTemplateGallery?: () => void;
};

export const TemplateGalleryBlockMenuSection = ({
	children,
}: {
	children: React.ReactNode;
}): React.JSX.Element => (
	<ToolbarDropdownItemSection hasSeparator>{children}</ToolbarDropdownItemSection>
);

export const TemplateGalleryBlockMenuItem = ({
	api,
	onCreateSnippet,
	onOpenTemplateGallery,
}: Props): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const hasActivatedCreate = useRef(false);
	const preservedSelection = api?.blockControls?.sharedState.currentState()?.preservedSelection;
	const canCreateSnippet = Boolean(
		onCreateSnippet && preservedSelection && !preservedSelection.empty,
	);
	const onClick = useCallback(
		() => onOpenTemplateGallery && openTemplateGallerySafely(onOpenTemplateGallery),
		[onOpenTemplateGallery],
	);
	const onCreateClick = useCallback(() => {
		if (!onCreateSnippet || hasActivatedCreate.current) {
			return;
		}

		const selection = api?.blockControls?.sharedState.currentState()?.preservedSelection;
		const request = getCreateSnippetRequestFromSelection(selection);
		if (!request) {
			return;
		}

		hasActivatedCreate.current = true;
		api?.core?.actions.execute(({ tr }) => {
			api?.blockControls?.commands.toggleBlockMenu({ closeMenu: true })({ tr });
			api?.blockControls?.commands.stopPreservingSelection()({ tr });
			return tr;
		});

		try {
			onCreateSnippet(request);
		} catch (error) {
			logException(error as Error, {
				location: 'editor-plugin-snippets/createSnippetBlockMenuItem',
			});
		}
	}, [api, onCreateSnippet]);

	if (!canCreateSnippet && !onOpenTemplateGallery) {
		return null;
	}

	return (
		<ToolbarNestedDropdownMenu
			text={formatMessage(templateGalleryMessages.blockTemplates)}
			elemBefore={<TemplateIcon label="" size="small" />}
			elemAfter={<ChevronRightIcon label="" size="small" />}
			elemAfterText={
				<Box as="span" paddingInlineStart="space.075">
					<Lozenge appearance="new">{formatMessage(templateGalleryMessages.newLozenge)}</Lozenge>
				</Box>
			}
			shouldFitContainer={isExperimentEnabled('platform_editor_snippets_plugin_confluence')}
			shouldTitleWrap={false}
		>
			<ToolbarDropdownItemSection>
				{canCreateSnippet && (
					<ToolbarDropdownItem
						elemBefore={<SaveIcon label="" size="small" />}
						onClick={onCreateClick}
					>
						{formatMessage(messages.saveBlockTemplate)}
					</ToolbarDropdownItem>
				)}
				{onOpenTemplateGallery && (
					<ToolbarDropdownItem elemBefore={<SearchIcon label="" size="small" />} onClick={onClick}>
						{formatMessage(templateGalleryMessages.browseTemplates)}
					</ToolbarDropdownItem>
				)}
			</ToolbarDropdownItemSection>
		</ToolbarNestedDropdownMenu>
	);
};

const getCreateSnippetRequestFromSelection = (selection?: Selection) => {
	if (!selection || selection.empty) {
		return undefined;
	}

	try {
		const { schema } = selection.$from.doc.type;
		const emptyDoc = schema.node('doc', null, [schema.node('paragraph')]);
		const selectedDoc = new Transform(emptyDoc).replaceRange(0, 2, selection.content()).doc;
		const adf = { version: 1, ...selectedDoc.toJSON() } as ADFEntity;

		return getCreateSnippetRequest(adf, 'blockMenu');
	} catch (error) {
		logException(error as Error, {
			location: 'editor-plugin-snippets/createSnippetBlockMenuSelection',
		});
		return undefined;
	}
};
